import React, { useState, useEffect } from "react";
import api, { API } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import PageHeader from "@/components/common/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtINR } from "@/lib/format";
import { Play, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { useDrillDown, DrillProvider, useOptionalDrill } from "@/components/reports/DrillDownDrawer";
import SendReportButton from "@/components/common/SendReportButton";

const today = () => new Date().toISOString().slice(0, 10);
const fyStart = () => {
  const t = new Date();
  const y = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
  return `${y}-04-01`;
};

async function downloadReport(path, params, filename) {
  const ok = await downloadBlob(path, filename, params);
  if (ok) toast.success("Downloaded " + filename);
}

function ParamPanel({ children, onRun, running, onExport, exportBusy, reportLabel }) {
  return (
    <div className="card-sharp p-4 flex flex-wrap gap-3 items-end mb-4" data-testid="param-panel">
      {children}
      <div className="ml-auto flex gap-2">
        {reportLabel && <SendReportButton reportLabel={reportLabel} />}
        {onExport && (
          <Button variant="outline" onClick={onExport} disabled={exportBusy} data-testid="export-btn">
            {exportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" />Export Excel</>}
          </Button>
        )}
        <Button onClick={onRun} disabled={running} data-testid="run-btn">
          {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : <><Play className="w-4 h-4 mr-2" /> Run report</>}
        </Button>
      </div>
    </div>
  );
}

function SalesRegister() {
  const [fr, setFr] = useState(fyStart());
  const [to, setTo] = useState(today());
  const [partyId, setPartyId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const drill = useOptionalDrill();
  useEffect(() => { api.get("/parties", { params: { party_type: "customer" } }).then(r => setCustomers(r.data || [])); }, []);
  const params = () => ({ from_date: fr, to_date: to, ...(partyId ? { party_id: partyId } : {}) });
  const run = async () => { setBusy(true); try { setData((await api.get("/reports/sales-register", { params: params() })).data); } finally { setBusy(false); } };
  const doExport = async () => { setExportBusy(true); await downloadReport("/reports/sales-register/export", params(), `sales-register_${fr}_to_${to}.xlsx`); setExportBusy(false); };
  // Group rows by month for interleaved subtotal rows
  const rowsByMonth = React.useMemo(() => {
    if (!data?.rows) return {};
    return data.rows.reduce((acc, r) => {
      const m = (r.date || "UNKNOWN").slice(0, 7);
      (acc[m] = acc[m] || []).push(r);
      return acc;
    }, {});
  }, [data]);
  const monthsSorted = React.useMemo(() => Object.keys(rowsByMonth).sort(), [rowsByMonth]);
  const subtotalByMonth = React.useMemo(() => {
    const out = {};
    (data?.monthly_subtotals || []).forEach((m) => { out[m.month] = m; });
    return out;
  }, [data]);
  return (
    <div data-testid="sales-register">
      <ParamPanel onRun={run} running={busy} onExport={doExport} exportBusy={exportBusy} reportLabel="Sales Register">
        <div><Label>From</Label><Input type="date" value={fr} onChange={e => setFr(e.target.value)} className="mt-1" data-testid="sr-from" /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-1" data-testid="sr-to" /></div>
        <div className="min-w-[220px]"><Label>Customer (optional)</Label>
          <Select value={partyId || "__all"} onValueChange={(v) => setPartyId(v === "__all" ? "" : v)}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="sr-customer"><SelectValue placeholder="All customers" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">All customers</SelectItem>{customers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </ParamPanel>
      {data && (
        <div className="card-sharp overflow-hidden" data-testid="sr-table">
          <div className="report-header">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Sales Register</div>
              <div className="text-sm font-medium">Period: {data.period.from} → {data.period.to} · {data.totals.count || data.rows.length} invoices</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dense">
              <thead>
                <tr>
                  <th>Date</th><th>Invoice #</th><th>Customer</th><th>GSTIN</th><th>PoS</th>
                  <th className="text-right">Subtotal</th>
                  <th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th>
                  <th className="text-right">Tax Total</th>
                  <th className="text-right">Total</th><th className="text-right">Paid</th><th className="text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {monthsSorted.map((month) => (
                  <React.Fragment key={month}>
                    <tr className="section-header">
                      <td colSpan={13}>{new Date(month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</td>
                    </tr>
                    {rowsByMonth[month].map((r, i) => (
                      <tr key={month + "-" + i} className="cursor-pointer" onClick={() => drill.openVoucherByNumber(r.invoice_number)} data-testid={`sr-row-${month}-${i}`}>
                        <td className="text-xs">{r.date}</td>
                        <td className="font-mono text-xs text-accent hover:underline">{r.invoice_number}</td>
                        <td>{r.customer_name}</td>
                        <td className={`font-mono text-xs ${r.customer_gstin === "URD" ? "text-amber-600" : ""}`}>{r.customer_gstin}</td>
                        <td className="text-xs">{r.customer_state_code || "—"}</td>
                        <td className="text-right tabular-nums">{fmtINR(r.subtotal)}</td>
                        <td className="text-right tabular-nums text-xs">{r.cgst > 0 ? fmtINR(r.cgst) : "—"}</td>
                        <td className="text-right tabular-nums text-xs">{r.sgst > 0 ? fmtINR(r.sgst) : "—"}</td>
                        <td className="text-right tabular-nums text-xs">{r.igst > 0 ? fmtINR(r.igst) : "—"}</td>
                        <td className="text-right tabular-nums font-medium">{fmtINR(r.tax)}</td>
                        <td className="text-right tabular-nums font-medium">{fmtINR(r.total)}</td>
                        <td className="text-right tabular-nums text-xs">{fmtINR(r.paid)}</td>
                        <td className="text-right tabular-nums">{fmtINR(r.balance)}</td>
                      </tr>
                    ))}
                    {subtotalByMonth[month] && (
                      <tr className="subtotal-row" data-testid={`sr-subtotal-${month}`}>
                        <td colSpan={5}>Subtotal · {new Date(month + "-01").toLocaleDateString("en-IN", { month: "short", year: "2-digit" })} · {subtotalByMonth[month].count} inv</td>
                        <td className="text-right tabular-nums">{fmtINR(subtotalByMonth[month].subtotal)}</td>
                        <td className="text-right tabular-nums text-xs">{fmtINR(subtotalByMonth[month].cgst)}</td>
                        <td className="text-right tabular-nums text-xs">{fmtINR(subtotalByMonth[month].sgst)}</td>
                        <td className="text-right tabular-nums text-xs">{fmtINR(subtotalByMonth[month].igst)}</td>
                        <td className="text-right tabular-nums">{fmtINR(subtotalByMonth[month].tax)}</td>
                        <td className="text-right tabular-nums">{fmtINR(subtotalByMonth[month].total)}</td>
                        <td className="text-right tabular-nums text-xs">{fmtINR(subtotalByMonth[month].paid)}</td>
                        <td className="text-right tabular-nums">{fmtINR(subtotalByMonth[month].balance)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                <tr className="total-row">
                  <td colSpan={5}>GRAND TOTAL · {data.totals.count || data.rows.length} invoices</td>
                  <td className="text-right tabular-nums">{fmtINR(data.totals.subtotal)}</td>
                  <td colSpan={3} className="text-right tabular-nums">—</td>
                  <td className="text-right tabular-nums">{fmtINR(data.totals.tax)}</td>
                  <td className="text-right tabular-nums">{fmtINR(data.totals.total)}</td>
                  <td className="text-right tabular-nums">{fmtINR(data.totals.paid)}</td>
                  <td className="text-right tabular-nums">{fmtINR(data.totals.balance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function PurchaseRegister() {
  const [fr, setFr] = useState(fyStart()); const [to, setTo] = useState(today());
  const [partyId, setPartyId] = useState(""); const [vendors, setVendors] = useState([]);
  const [data, setData] = useState(null); const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const drill = useOptionalDrill();
  useEffect(() => { api.get("/parties", { params: { party_type: "vendor" } }).then(r => setVendors(r.data || [])); }, []);
  const params = () => ({ from_date: fr, to_date: to, ...(partyId ? { party_id: partyId } : {}) });
  const run = async () => { setBusy(true); try { setData((await api.get("/reports/purchase-register", { params: params() })).data); } finally { setBusy(false); } };
  const doExport = async () => { setExportBusy(true); await downloadReport("/reports/purchase-register/export", params(), `purchase-register_${fr}_to_${to}.xlsx`); setExportBusy(false); };
  const rowsByMonth = React.useMemo(() => {
    if (!data?.rows) return {};
    return data.rows.reduce((acc, r) => {
      const m = (r.date || "UNKNOWN").slice(0, 7);
      (acc[m] = acc[m] || []).push(r);
      return acc;
    }, {});
  }, [data]);
  const monthsSorted = React.useMemo(() => Object.keys(rowsByMonth).sort(), [rowsByMonth]);
  const subtotalByMonth = React.useMemo(() => {
    const out = {};
    (data?.monthly_subtotals || []).forEach((m) => { out[m.month] = m; });
    return out;
  }, [data]);
  return (
    <div data-testid="purchase-register">
      <ParamPanel onRun={run} running={busy} onExport={doExport} exportBusy={exportBusy} reportLabel="Purchase Register">
        <div><Label>From</Label><Input type="date" value={fr} onChange={e => setFr(e.target.value)} className="mt-1" data-testid="pr-from" /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-1" data-testid="pr-to" /></div>
        <div className="min-w-[220px]"><Label>Vendor (optional)</Label>
          <Select value={partyId || "__all"} onValueChange={(v) => setPartyId(v === "__all" ? "" : v)}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="pr-vendor"><SelectValue placeholder="All vendors" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">All vendors</SelectItem>{vendors.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </ParamPanel>
      {data && (
        <div className="card-sharp overflow-hidden" data-testid="pr-table">
          <div className="report-header">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Purchase Register</div>
              <div className="text-sm font-medium">Period: {data.period.from} → {data.period.to} · {data.totals.count || data.rows.length} bills</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dense">
              <thead>
                <tr>
                  <th>Date</th><th>Bill #</th><th>Vendor</th><th>GSTIN</th><th>State</th>
                  <th className="text-right">Subtotal</th>
                  <th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th>
                  <th className="text-right">Tax Total</th>
                  <th className="text-right">Total</th><th className="text-right">Paid</th><th className="text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {monthsSorted.map((month) => (
                  <React.Fragment key={month}>
                    <tr className="section-header">
                      <td colSpan={13}>{new Date(month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</td>
                    </tr>
                    {rowsByMonth[month].map((r, i) => (
                      <tr key={month + "-" + i} className="cursor-pointer" onClick={() => drill.openVoucherByNumber(r.bill_number)} data-testid={`pr-row-${month}-${i}`}>
                        <td className="text-xs">{r.date}</td>
                        <td className="font-mono text-xs text-accent hover:underline">{r.bill_number}</td>
                        <td>{r.vendor_name}</td>
                        <td className={`font-mono text-xs ${r.vendor_gstin === "URD" ? "text-amber-600" : ""}`}>{r.vendor_gstin}</td>
                        <td className="text-xs">{r.vendor_state_code || "—"}</td>
                        <td className="text-right tabular-nums">{fmtINR(r.subtotal)}</td>
                        <td className="text-right tabular-nums text-xs">{r.cgst > 0 ? fmtINR(r.cgst) : "—"}</td>
                        <td className="text-right tabular-nums text-xs">{r.sgst > 0 ? fmtINR(r.sgst) : "—"}</td>
                        <td className="text-right tabular-nums text-xs">{r.igst > 0 ? fmtINR(r.igst) : "—"}</td>
                        <td className="text-right tabular-nums font-medium">{fmtINR(r.tax)}</td>
                        <td className="text-right tabular-nums font-medium">{fmtINR(r.total)}</td>
                        <td className="text-right tabular-nums text-xs">{fmtINR(r.paid)}</td>
                        <td className="text-right tabular-nums">{fmtINR(r.balance)}</td>
                      </tr>
                    ))}
                    {subtotalByMonth[month] && (
                      <tr className="subtotal-row" data-testid={`pr-subtotal-${month}`}>
                        <td colSpan={5}>Subtotal · {new Date(month + "-01").toLocaleDateString("en-IN", { month: "short", year: "2-digit" })} · {subtotalByMonth[month].count} bills</td>
                        <td className="text-right tabular-nums">{fmtINR(subtotalByMonth[month].subtotal)}</td>
                        <td className="text-right tabular-nums text-xs">{fmtINR(subtotalByMonth[month].cgst)}</td>
                        <td className="text-right tabular-nums text-xs">{fmtINR(subtotalByMonth[month].sgst)}</td>
                        <td className="text-right tabular-nums text-xs">{fmtINR(subtotalByMonth[month].igst)}</td>
                        <td className="text-right tabular-nums">{fmtINR(subtotalByMonth[month].tax)}</td>
                        <td className="text-right tabular-nums">{fmtINR(subtotalByMonth[month].total)}</td>
                        <td className="text-right tabular-nums text-xs">{fmtINR(subtotalByMonth[month].paid)}</td>
                        <td className="text-right tabular-nums">{fmtINR(subtotalByMonth[month].balance)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                <tr className="total-row">
                  <td colSpan={5}>GRAND TOTAL · {data.totals.count || data.rows.length} bills</td>
                  <td className="text-right tabular-nums">{fmtINR(data.totals.subtotal)}</td>
                  <td colSpan={3} className="text-right tabular-nums">—</td>
                  <td className="text-right tabular-nums">{fmtINR(data.totals.tax)}</td>
                  <td className="text-right tabular-nums">{fmtINR(data.totals.total)}</td>
                  <td className="text-right tabular-nums">{fmtINR(data.totals.paid)}</td>
                  <td className="text-right tabular-nums">{fmtINR(data.totals.balance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function GeneralLedger() {
  const [fr, setFr] = useState(fyStart()); const [to, setTo] = useState(today());
  const [accounts, setAccounts] = useState([]); const [selected, setSelected] = useState({});
  const [data, setData] = useState(null); const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [pickerFilter, setPickerFilter] = useState("");
  const drill = useOptionalDrill();
  useEffect(() => { api.get("/accounts").then(r => setAccounts(r.data || [])); }, []);
  const params = () => {
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k).join(",");
    return { from_date: fr, to_date: to, ...(ids ? { account_ids: ids } : {}) };
  };
  const run = async () => { setBusy(true); try { setData((await api.get("/reports/general-ledger", { params: params() })).data); } finally { setBusy(false); } };
  const doExport = async () => { setExportBusy(true); await downloadReport("/reports/general-ledger/export", params(), `general-ledger_${fr}_to_${to}.xlsx`); setExportBusy(false); };
  const visibleAccounts = accounts.filter(a => {
    if (!pickerFilter) return true;
    const q = pickerFilter.toLowerCase();
    return (a.name || "").toLowerCase().includes(q) || (a.code || "").toLowerCase().includes(q) || (a.account_type || "").toLowerCase().includes(q);
  });
  const toggleAllVisible = (v) => {
    const s = { ...selected };
    visibleAccounts.forEach(a => { s[a.id] = v; });
    setSelected(s);
  };
  const selectedCount = Object.values(selected).filter(Boolean).length;
  return (
    <div data-testid="general-ledger">
      <ParamPanel onRun={run} running={busy} onExport={doExport} exportBusy={exportBusy} reportLabel="General Ledger">
        <div><Label>From</Label><Input type="date" value={fr} onChange={e => setFr(e.target.value)} className="mt-1" data-testid="gl-from" /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-1" data-testid="gl-to" /></div>
      </ParamPanel>
      <div className="card-sharp mb-4" data-testid="gl-account-picker">
        <div className="p-3 border-b bg-muted/30 flex flex-wrap items-center gap-2 sticky top-0 z-10">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">
            Accounts <span className="text-foreground">({visibleAccounts.length}/{accounts.length})</span> — leave blank to include ALL · <span className="text-emerald-600">{selectedCount} selected</span>
          </div>
          <Input
            placeholder="Search account by code, name, or type…"
            value={pickerFilter}
            onChange={e => setPickerFilter(e.target.value)}
            className="h-8 text-xs max-w-xs ml-2"
            data-testid="gl-picker-search"
          />
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => toggleAllVisible(true)} data-testid="gl-select-all" className="h-8 text-xs">Select All {pickerFilter ? "Visible" : ""}</Button>
            <Button size="sm" variant="outline" onClick={() => toggleAllVisible(false)} data-testid="gl-clear-all" className="h-8 text-xs">Clear</Button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-1">
            {visibleAccounts.length === 0 && (
              <div className="col-span-full text-center py-6 text-muted-foreground text-xs">No accounts match "{pickerFilter}"</div>
            )}
            {visibleAccounts.map(a => (
              <label key={a.id} className="flex items-center gap-2 text-xs py-1.5 px-2 cursor-pointer rounded-sm hover:bg-muted/40">
                <Checkbox checked={!!selected[a.id]} onCheckedChange={(v) => setSelected({ ...selected, [a.id]: !!v })} data-testid={`gl-acct-${a.code}`} />
                <span className="font-mono text-muted-foreground w-14 shrink-0">{a.code}</span>
                <span className="flex-1 min-w-0 truncate" title={a.name}>{a.name}</span>
                <span className="text-[9px] font-mono uppercase text-muted-foreground/70 shrink-0">{a.account_type}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      {data && data.accounts.map((acc, idx) => (
        <div key={idx} className="card-sharp p-4 mb-3" data-testid={`gl-acc-${acc.account_code}`}>
          <div className="flex items-baseline gap-3 border-b pb-2 mb-2">
            <span className="font-mono text-xs">{acc.account_code}</span>
            <span className="font-semibold">{acc.account_name}</span>
            <span className="text-[10px] font-mono uppercase text-muted-foreground ml-auto">Opening: <span className="font-mono">{fmtINR(acc.opening_balance)}</span></span>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b"><th className="text-left py-1">Date</th><th className="text-left">Voucher</th><th className="text-left">Type</th><th className="text-left">Narration</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th></tr></thead>
            <tbody>
              {acc.lines.map((l, i) => (
                <tr key={i} className="border-b cursor-pointer hover:bg-muted/40" onClick={() => drill.openVoucherByNumber(l.voucher_number)} data-testid={`gl-line-${idx}-${i}`}><td className="py-1 text-xs">{l.date}</td><td className="font-mono text-xs text-accent hover:underline">{l.voucher_number}</td><td className="text-[10px] font-mono uppercase text-accent">{l.voucher_type}</td><td className="text-xs truncate max-w-[240px]" title={l.narration}>{l.narration}</td><td className="text-right font-mono">{l.debit ? fmtINR(l.debit) : ""}</td><td className="text-right font-mono">{l.credit ? fmtINR(l.credit) : ""}</td><td className="text-right font-mono">{fmtINR(l.balance)}</td></tr>
              ))}
              <tr className="border-t-2 font-semibold"><td colSpan={4} className="py-2 text-right">Total / Closing</td><td className="text-right font-mono">{fmtINR(acc.total_debit)}</td><td className="text-right font-mono">{fmtINR(acc.total_credit)}</td><td className="text-right font-mono">{fmtINR(acc.closing_balance)}</td></tr>
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function ARAP({ kind }) {
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null); const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const url = kind === "ar" ? "/reports/ar-details" : "/reports/ap-details";
  const run = async () => { setBusy(true); try { setData((await api.get(url, { params: { as_of: asOf } })).data); } finally { setBusy(false); } };
  const doExport = async () => { setExportBusy(true); await downloadReport(`${url}/export`, { as_of: asOf }, `${kind}-details_${asOf}.xlsx`); setExportBusy(false); };
  const buckets = [["0_30", "0-30 d"], ["31_60", "31-60 d"], ["61_90", "61-90 d"], ["91_180", "91-180 d"], ["gt_180", "> 180 d"]];
  const title = kind === "ar" ? "Accounts Receivable" : "Accounts Payable";
  return (
    <div data-testid={`${kind}-details`}>
      <ParamPanel onRun={run} running={busy} onExport={doExport} exportBusy={exportBusy} reportLabel={title}>
        <div><Label>As of</Label><Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="mt-1" data-testid={`${kind}-as-of`} /></div>
      </ParamPanel>
      {data && (
        <div className="card-sharp p-4 overflow-x-auto" data-testid={`${kind}-table`}>
          <div className="text-xs text-muted-foreground mb-2">As of {data.as_of} · {data.rows.length} parties · Grand Total: <span className="font-mono">{fmtINR(data.grand_total)}</span></div>
          <table className="w-full text-sm">
            <thead><tr className="border-b"><th className="text-left py-2">{title.split(" ")[1]}</th><th className="text-left">GSTIN</th><th className="text-left">State</th>{buckets.map(([k, l]) => <th key={k} className="text-right">{l}</th>)}<th className="text-right">Total Outstanding</th></tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.party_id} className="border-b"><td className="py-1.5">{r.party_name}{r.is_msme && <span className="ml-2 text-[10px] font-mono uppercase text-orange-500">MSME</span>}</td><td className="font-mono text-xs">{r.gstin}</td><td className="text-xs">{r.state}</td>{buckets.map(([k]) => <td key={k} className="text-right font-mono">{fmtINR(r.buckets[k])}</td>)}<td className="text-right font-mono font-semibold">{fmtINR(r.total_outstanding)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function JournalRegister() {
  const [fr, setFr] = useState(fyStart()); const [to, setTo] = useState(today());
  const [data, setData] = useState(null); const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const drill = useOptionalDrill();
  const params = () => ({ from_date: fr, to_date: to });
  const run = async () => { setBusy(true); try { setData((await api.get("/reports/journal-register", { params: params() })).data); } finally { setBusy(false); } };
  const doExport = async () => { setExportBusy(true); await downloadReport("/reports/journal-register/export", params(), `journal-register_${fr}_to_${to}.xlsx`); setExportBusy(false); };
  return (
    <div data-testid="journal-register">
      <ParamPanel onRun={run} running={busy} onExport={doExport} exportBusy={exportBusy} reportLabel="Journal Register">
        <div><Label>From</Label><Input type="date" value={fr} onChange={e => setFr(e.target.value)} className="mt-1" data-testid="jr-from" /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-1" data-testid="jr-to" /></div>
      </ParamPanel>
      {data && (
        <div className="card-sharp p-4 overflow-x-auto" data-testid="jr-table">
          <div className="text-xs text-muted-foreground mb-2">Period: {data.period.from} → {data.period.to} · {data.rows.length} journal lines</div>
          <table className="w-full text-sm">
            <thead><tr className="border-b"><th className="text-left py-2">Date</th><th className="text-left">Voucher #</th><th className="text-left">GL Code</th><th className="text-left">GL Name</th><th className="text-left">Narration</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-b cursor-pointer hover:bg-muted/40" onClick={() => drill.openVoucherByNumber(r.voucher_number)} data-testid={`jr-row-${i}`}><td className="py-1.5 text-xs">{r.date}</td><td className="font-mono text-xs text-accent hover:underline">{r.voucher_number}</td><td className="font-mono text-xs">{r.account_code}</td><td>{r.account_name}</td><td className="text-xs truncate max-w-[240px]" title={r.narration}>{r.narration}</td><td className="text-right font-mono">{r.debit ? fmtINR(r.debit) : ""}</td><td className="text-right font-mono">{r.credit ? fmtINR(r.credit) : ""}</td></tr>
              ))}
              <tr className="border-t-2 font-semibold"><td colSpan={5} className="py-2 text-right">Total</td><td className="text-right font-mono">{fmtINR(data.totals.debit)}</td><td className="text-right font-mono">{fmtINR(data.totals.credit)}</td></tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CashBankBook({ kind }) {   // kind = "cash" | "bank"
  const [fr, setFr] = useState(fyStart()); const [to, setTo] = useState(today());
  const [accountId, setAccountId] = useState("");
  const [ledgers, setLedgers] = useState([]);
  const [data, setData] = useState(null); const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const drill = useOptionalDrill();
  const label = kind === "cash" ? "Cash" : "Bank";
  useEffect(() => {
    api.get("/accounts").then(r => setLedgers((r.data || []).filter(a => a.subtype === kind)));
  }, [kind]);
  const params = () => ({ from_date: fr, to_date: to, ...(accountId ? { account_id: accountId } : {}) });
  const url = kind === "cash" ? "/reports/cash-book" : "/reports/bank-book";
  const run = async () => { setBusy(true); try { setData((await api.get(url, { params: params() })).data); } finally { setBusy(false); } };
  const doExport = async () => { setExportBusy(true); await downloadReport(`${url}/export`, params(), `${kind}-book_${fr}_to_${to}.xlsx`); setExportBusy(false); };
  return (
    <div data-testid={`${kind}-book`}>
      <ParamPanel onRun={run} running={busy} onExport={doExport} exportBusy={exportBusy} reportLabel={`${label} Book`}>
        <div><Label>From</Label><Input type="date" value={fr} onChange={e => setFr(e.target.value)} className="mt-1" data-testid={`${kind}-from`} /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-1" data-testid={`${kind}-to`} /></div>
        <div className="min-w-[240px]"><Label>{label} ledger (optional)</Label>
          <Select value={accountId || "__all"} onValueChange={(v) => setAccountId(v === "__all" ? "" : v)}>
            <SelectTrigger className="rounded-sm mt-1" data-testid={`${kind}-ledger-picker`}><SelectValue placeholder={`All ${label} ledgers`} /></SelectTrigger>
            <SelectContent><SelectItem value="__all">All {label} ledgers</SelectItem>{ledgers.map(a => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </ParamPanel>
      {data && (
        <div className="card-sharp p-4 overflow-x-auto" data-testid={`${kind}-table`}>
          <div className="text-xs text-muted-foreground mb-2">
            Period: {data.period.from} → {data.period.to} ·
            {" "}{data.accounts.length} ledger(s) · Opening: <span className="font-mono">{fmtINR(data.opening_balance)}</span> ·
            {" "}Closing: <span className="font-mono font-semibold">{fmtINR(data.closing_balance)}</span>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b"><th className="text-left py-2">Date</th><th className="text-left">Voucher #</th><th className="text-left">Ledger</th><th className="text-left">Contra</th><th className="text-left">Narration</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th></tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-b cursor-pointer hover:bg-muted/40" onClick={() => drill.openVoucherByNumber(r.voucher_number)} data-testid={`${kind}-row-${i}`}><td className="py-1.5 text-xs">{r.date}</td><td className="font-mono text-xs text-accent hover:underline">{r.voucher_number}</td><td>{r.account_name}</td><td className="text-xs">{r.contra}</td><td className="text-xs truncate max-w-[240px]" title={r.narration}>{r.narration}</td><td className="text-right font-mono">{r.debit ? fmtINR(r.debit) : ""}</td><td className="text-right font-mono">{r.credit ? fmtINR(r.credit) : ""}</td><td className="text-right font-mono">{fmtINR(r.balance)}</td></tr>
              ))}
              <tr className="border-t-2 font-semibold"><td colSpan={5} className="py-2 text-right">Total / Closing</td><td className="text-right font-mono">{fmtINR(data.totals.debit)}</td><td className="text-right font-mono">{fmtINR(data.totals.credit)}</td><td className="text-right font-mono">{fmtINR(data.closing_balance)}</td></tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Registers() {
  const drill = useDrillDown();
  return (
    <DrillProvider drill={drill}>
      <div className="p-6 lg:p-8" data-testid="registers-page">
        <PageHeader eyebrow="Registers & Statements" title="Sales · Purchase · GL · Journal · Cash · Bank · AR · AP" description="Click any voucher # to open the full voucher detail — deep-dive without leaving the report." />
        <Tabs defaultValue="sales">
          <TabsList className="rounded-sm flex-wrap h-auto">
            <TabsTrigger value="sales" className="rounded-sm" data-testid="tab-sales-register">Sales</TabsTrigger>
            <TabsTrigger value="purchase" className="rounded-sm" data-testid="tab-purchase-register">Purchase</TabsTrigger>
            <TabsTrigger value="gl" className="rounded-sm" data-testid="tab-gl">General Ledger</TabsTrigger>
            <TabsTrigger value="journal" className="rounded-sm" data-testid="tab-journal">Journal</TabsTrigger>
            <TabsTrigger value="cash" className="rounded-sm" data-testid="tab-cash-book">Cash Book</TabsTrigger>
            <TabsTrigger value="bank" className="rounded-sm" data-testid="tab-bank-book">Bank Book</TabsTrigger>
            <TabsTrigger value="ar" className="rounded-sm" data-testid="tab-ar">AR Details</TabsTrigger>
            <TabsTrigger value="ap" className="rounded-sm" data-testid="tab-ap">AP Details</TabsTrigger>
          </TabsList>
          <TabsContent value="sales" className="mt-4"><SalesRegister /></TabsContent>
          <TabsContent value="purchase" className="mt-4"><PurchaseRegister /></TabsContent>
          <TabsContent value="gl" className="mt-4"><GeneralLedger /></TabsContent>
          <TabsContent value="journal" className="mt-4"><JournalRegister /></TabsContent>
          <TabsContent value="cash" className="mt-4"><CashBankBook kind="cash" /></TabsContent>
          <TabsContent value="bank" className="mt-4"><CashBankBook kind="bank" /></TabsContent>
          <TabsContent value="ar" className="mt-4"><ARAP kind="ar" /></TabsContent>
          <TabsContent value="ap" className="mt-4"><ARAP kind="ap" /></TabsContent>
        </Tabs>
      </div>
    </DrillProvider>
  );
}
